/**
 * Client-side CSV export. Excel-friendly (UTF-8 BOM, CRLF, RFC 4180 quoting).
 * Ported verbatim from the legacy Pulse UI so operators get the same
 * behaviour they've muscle-memoried:
 *   - `\uFEFF` byte-order mark so Excel opens Indian rupee glyphs correctly.
 *   - CRLF line endings — Windows still bites otherwise.
 *   - `"…""…"` quoting when the value contains a comma, quote, LF, or CR.
 *   - Filename gets a date stamp via {@link stampName} so multiple exports
 *     in a day don't overwrite each other.
 *
 * `downloadCsv` triggers a real DOM download via a synthetic anchor; browser
 * environments (jsdom / happy-dom) can exercise the string assembly with
 * {@link renderCsv}, which is what the tests target.
 */

export interface CsvCol<T> {
  label: string
  value: (row: T) => string | number | null | undefined
}

const BOM = '\uFEFF'
const LINE_END = '\r\n'

function escape(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/**
 * Build the CSV string for a row set + column set. Exposed so tests can
 * assert the exact byte sequence without touching the DOM.
 */
export function renderCsv<T>(rows: T[], cols: CsvCol<T>[]): string {
  const head = cols.map(c => escape(c.label)).join(',')
  if (rows.length === 0) return BOM + head
  const body = rows.map(r => cols.map(c => escape(c.value(r))).join(',')).join(LINE_END)
  return BOM + head + LINE_END + body
}

export function downloadCsv<T>(filename: string, rows: T[], cols: CsvCol<T>[]): void {
  const csv = renderCsv(rows, cols)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** A dated filename stem, e.g. "pulse-pos-2026-08-26". */
export function stampName(stem: string, now: Date = new Date()): string {
  return `${stem}-${now.toISOString().slice(0, 10)}`
}
